// Wayfinding palette (Aug 18, per Ali's report) — replaces the earlier
// "Golden Hour" warm-cream/orange palette entirely, following approval
// of the Boarding Pass structure + Wayfinding navy/signal-yellow color
// mockup. Every key name below is UNCHANGED from the old palette on
// purpose: every component that already reads theme.structural,
// theme.action, theme.bg, etc. picks up the new colors automatically,
// with zero risk to the logic those components carry — only the color
// VALUES changed here, not what each token means or where it's used.
// Grounded in real airport wayfinding signage (the typeface family and
// color logic actual airports use), not a generic "modern app" look —
// see the approved reference mockup for the reasoning against the
// three most common AI-generated design defaults.

export const theme = {
  bg: '#EEF1F3',             // page background — cool signage gray-blue
                              // (was '#FAF8F5' warm cream)
  surface: '#FFFFFF',        // card background — unchanged, still true white
  border: '#1E3A5F',         // card borders — now a strong 2px navy line
                              // (was '#EFEDE8' a barely-there warm gray).
                              // The approved reference shows every card as
                              // a crisp bordered plate, not a shadow-lifted
                              // surface — this token is what draws that
                              // border everywhere theme.border already
                              // gets used as a card/section boundary.

  structural: '#1E3A5F',    // nav bar, header bands, secondary buttons —
                             // same navy as `border` on purpose, this is
                             // the one signature color of the whole theme
                             // (was '#3B5B7D')
  action: '#F4C11C',        // the ONE primary action color per screen —
                             // signal yellow, matching the approved
                             // reference's "URGENT" stub and primary CTA
                             // buttons (was '#E2703F' orange). Still
                             // reserved for exactly one highest-intent
                             // action per screen — that discipline from
                             // the earlier CTA-hierarchy work carries
                             // over unchanged, only the color itself moved.
  scarcity: '#C1592B',       // UNCHANGED — ribbon/urgency-badge language
                              // ("Rare find", "New low") stays a distinct
                              // rust tone from both `action` (primary CTA)
                              // and the urgent-stub yellow, so a decorative
                              // "this is a good find" ribbon doesn't get
                              // confused with "click this button" or
                              // "this needs a decision" — three adjacent
                              // but genuinely different signals.
  urgentBadge: '#F4C11C',   // urgent badge fill — now the same signal
                             // yellow as `action`, matching the approved
                             // stub color exactly (was '#F4C978' melon)
  urgentBadgeText: '#1a1a1a',

  textPrimary: '#1E3A5F',   // was '#2B2620' warm brown — headline text is
                             // now navy, matching the approved mockups
  textMuted: '#44566B',     // was '#7A736A' warm gray — cooler gray to
                             // match the signage palette

  success: '#2D5F3E',        // was '#4A9B6E' — a more grounded, less minty
                              // green that sits better against navy+yellow
                              // than the old warm-palette green did.
                              // Still reserved strictly for savings/success,
                              // unchanged from that Aug 16 discipline.
  danger: '#B23B3B',         // was '#f87171' — kept distinct from both
                              // `scarcity` (rust, exciting) and `success`
                              // (green, good) so "over budget" reads as
                              // its own clear signal, not blurred into
                              // either adjacent color's meaning
  warning: '#D98C3D',        // unchanged — staleness warning
  freshnessOk: '#2D5F3E',
  freshnessAging: '#D98C3D',

  inactiveGray: '#DDE3E7',   // was '#E8E1D6' warm beige-gray — cooler
                              // gray matching the new input/toggle borders
  inactiveGrayText: '#5A6B78',

  // cream renamed in spirit but not in key (same reason as every other
  // token above — avoid a hunt-and-replace across 4 files): this now
  // holds WHITE, since nav-bar/badge text on the navy surface is white
  // in the approved reference, not a cream tint (was '#FDF6EC').
  cream: '#FFFFFF',

  // New tokens, additive only — nothing existing reads these yet, so
  // adding them can't break anything. Available for the perforated
  // ticket-stub structure and monospace data treatment as components
  // are updated to use them.
  divider: '#DDE3E7',        // lighter internal dividers, distinct from
                              // the heavier navy `border` used for card
                              // edges
  quietStub: '#7B93AC',      // Quietly Watching stub fill (Aug 18, per
                              // Ali's report — Option B of three mocked
                              // treatments) — a deliberately desaturated
                              // third tone so a quiet/watching pin reads
                              // as visually distinct from the yellow
                              // urgent stub and navy frozen stub at a
                              // glance across a page full of cards,
                              // without introducing a fourth unrelated
                              // hue into the palette.
  fontDisplay: "'Public Sans', sans-serif",
  fontMono: "'JetBrains Mono', monospace",
};

export default theme;
